import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, FileText, ClipboardCheck, ExternalLink } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, StatusBadge } from './shared';

const fmtDate = (s) => {
  if (!s) return '—';
  // Accept both ISO datetimes and plain YYYY-MM-DD.
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const vehicleLabel = (v = {}) => {
  const parts = [v.make, v.model].filter(Boolean).join(' ').trim();
  const reg = (v.registration_number || '').trim();
  if (parts && reg) return `${parts} · ${reg}`;
  return parts || reg || 'Vehicle';
};

/**
 * Service history modal for a single vehicle on a customer profile.
 * Lists inspections (with link back to edit) + invoices (with link to PDF).
 */
const VehicleHistoryModal = ({ customer, vehicle, onClose }) => {
  const { authHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [data, setData] = useState({ inspections: [], invoices: [] });

  const fetchHistory = useCallback(async () => {
    if (!customer?.id || !vehicle?.id) return;
    setLoading(true);
    setErr('');
    try {
      const { data: resp } = await axios.get(
        `${API}/billing/customers/${customer.id}/vehicles/${vehicle.id}/history`,
        { headers: authHeader() },
      );
      setData({
        inspections: resp.inspections || [],
        invoices: resp.invoices || [],
      });
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader, customer, vehicle]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const totalBilled = (data.invoices || []).reduce(
    (acc, inv) => acc + Number(inv.total_due || 0),
    0,
  );
  const outstanding = (data.invoices || [])
    .filter((inv) => !['paid', 'cancelled'].includes(inv.status))
    .reduce((acc, inv) => acc + Math.max(0, Number(inv.total_due || 0) - Number(inv.amount_paid || 0)), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="vehicle-history-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Service history — {vehicleLabel(vehicle)}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {[customer.prefix, customer.name].filter(Boolean).join(' ')}
              {customer.phone ? ` · ${customer.phone}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="vehicle-history-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {loading ? (
            <div className="text-center text-slate-500 py-10">
              <Loader2 className="animate-spin inline mr-2" size={18} /> Loading history…
            </div>
          ) : err ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>
          ) : (
            <>
              {/* Summary chips */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="vehicle-history-summary">
                <SummaryChip label="Inspections" value={data.inspections.length} />
                <SummaryChip label="Invoices" value={data.invoices.length} />
                <SummaryChip label="Total billed" value={`₹ ${fmtINR(totalBilled)}`} />
                <SummaryChip label="Outstanding" value={`₹ ${fmtINR(outstanding)}`} accent={outstanding > 0 ? 'amber' : 'slate'} />
              </div>

              {/* Inspections */}
              <Section title="Inspections" icon={ClipboardCheck} count={data.inspections.length} testid="vehicle-history-inspections">
                {data.inspections.length === 0 ? (
                  <Empty text="No inspections recorded for this vehicle yet." />
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Serial</th>
                        <th className="text-left px-3 py-2 font-semibold">Date</th>
                        <th className="text-left px-3 py-2 font-semibold hidden sm:table-cell">Status</th>
                        <th className="text-right px-3 py-2 font-semibold">Estimate</th>
                        <th className="text-right px-3 py-2 font-semibold w-20">Open</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.inspections.map((insp) => (
                        <tr key={insp.id} className="border-t border-slate-200" data-testid={`vh-insp-${insp.id}`}>
                          <td className="px-3 py-2 font-medium text-slate-900">{insp.serial_no || insp.id.slice(0, 8)}</td>
                          <td className="px-3 py-2 text-slate-600">{fmtDate(insp.inspection_date || insp.created_at)}</td>
                          <td className="px-3 py-2 hidden sm:table-cell">
                            {insp.status ? (
                              <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{insp.status}</span>
                            ) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">
                            {insp.estimate ? `₹ ${fmtINR(insp.estimate)}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <a
                              href={`/admin/inspections/${insp.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-700 text-xs font-medium"
                            >
                              Open <ExternalLink size={11} />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>

              {/* Invoices */}
              <Section title="Invoices" icon={FileText} count={data.invoices.length} testid="vehicle-history-invoices">
                {data.invoices.length === 0 ? (
                  <Empty text="No invoices linked to this vehicle yet." />
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Number</th>
                        <th className="text-left px-3 py-2 font-semibold">Date</th>
                        <th className="text-left px-3 py-2 font-semibold hidden sm:table-cell">Status</th>
                        <th className="text-right px-3 py-2 font-semibold">Total</th>
                        <th className="text-right px-3 py-2 font-semibold w-20">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoices.map((inv) => (
                        <tr key={inv.id} className="border-t border-slate-200" data-testid={`vh-inv-${inv.id}`}>
                          <td className="px-3 py-2 font-medium text-slate-900">{inv.number}</td>
                          <td className="px-3 py-2 text-slate-600">{fmtDate(inv.date)}</td>
                          <td className="px-3 py-2 hidden sm:table-cell">
                            <StatusBadge status={inv.status} />
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700">₹ {fmtINR(inv.total_due)}</td>
                          <td className="px-3 py-2 text-right">
                            <a
                              href={`${API}/billing/invoices/${inv.id}/pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-700 text-xs font-medium"
                            >
                              PDF <ExternalLink size={11} />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryChip = ({ label, value, accent = 'slate' }) => {
  const accentCls = {
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
  }[accent] || 'bg-slate-50 border-slate-200 text-slate-900';
  return (
    <div className={`border rounded-lg px-3 py-2 ${accentCls}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-base font-semibold mt-0.5">{value}</div>
    </div>
  );
};

const Section = ({ title, icon: Icon, count, children, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} className="text-slate-500" />
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <span className="text-xs text-slate-500">({count})</span>
    </div>
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      {children}
    </div>
  </div>
);

const Empty = ({ text }) => (
  <div className="text-center text-slate-500 text-sm py-6">{text}</div>
);

export default VehicleHistoryModal;
